import React, { Dispatch, SetStateAction } from "react";
import useTgkFormAmountSourcePanel from "./TgkFormAmountSourcePanelHook";
import { CoObjectItem, TgkAmountObjectSource } from "../../interfaces/CoObject";
import { DbApplicationDropDown } from "../DbApplicationDropdown";
import { ChoiceGroup, IComboBoxOption } from "@fluentui/react";
import { FormDropDown } from "../co-dropdown-panel/form-drop-down/FormDropDown";
import { DropDownSheet } from "../co-dropdown-panel/drop-down-sheet/DropDownSheet";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { PasteObject } from "../PasteObject";
import { DropDownAreaAndChart } from "../co-dropdown-panel/drop-down-area-and-chart/DropDownAreaAndChart";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { TextField } from "@fluentui/react/lib/TextField";
import { InputAreaSelectionEnum } from "../../../utils/constants/InputAreaSelectionEnum";

export interface TgkFormAmountSourceProps {
  isEdit?: boolean;
  objectSource: TgkAmountObjectSource;
  updateSource: (obj: TgkAmountObjectSource, tabValid, modified: boolean) => void;
  validateSource: (obj: TgkAmountObjectSource) => ObjectPanelTabs;
  selectDBAndFetchDefaultRoots: (option: IComboBoxOption) => void;
  isFileExcelExisting?: boolean;
  isComponentInstance?: boolean;
  isLocked?: boolean;
  isLoadingDataExcel?: boolean;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  onPasteObject: (smartObject: CoObjectItem) => void;
}

export const TgkFormAmountSourcePanel = (props: TgkFormAmountSourceProps) => {
  const {
    objectSource,
    onFormClick,
    selectSheetComboBox,
    initAreas,
    sheetsAreaChartMap,
    selectedChoice,
    choiceOptions,
    onChangeChoice,
    selectAreaComboBox,
    onChangeCellValue,
    selectDBAndFetchDefaultRoots,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    hasSheetToBeSelected,
    selectedCellValue,
  } = useTgkFormAmountSourcePanel(props);

  const renderAreaOrCellSelection = () => {
    if (selectedChoice === InputAreaSelectionEnum.AREA_NAME.description) {
      return (
        <DropDownAreaAndChart
          isEdit={props.isEdit}
          name={objectSource.area}
          sheetsAreaChartMap={sheetsAreaChartMap}
          sheet={objectSource.sheet}
          selectNameComboBox={selectAreaComboBox}
          type="area"
          isFileExcelExisting={isFileExcelExisting}
          isComponentInstance={isComponentInstance}
          isLocked={isLocked}
          objectSource={objectSource.objectSource}
          isLoadingDataExcel={props.isLoadingDataExcel}
        />
      );
    } else {
      return (
        <TextField
          id="cell_edit"
          data-testId="cell-input"
          label={formatMessage("cof.objectsource.amount.excelpanel.selectcell")}
          required
          value={selectedCellValue}
          onChange={onChangeCellValue}
          disabled={objectSource.sheet == null || !isFileExcelExisting || isComponentInstance || isLocked}
        />
      );
    }
  };

  return (
    <div>
      {!isComponentInstance && <PasteObject onPasteObject={props.onPasteObject} objectType={objectSource.type} />}
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
        <DbApplicationDropDown
          disabled={isComponentInstance || isLocked}
          dbApplication={objectSource?.dbApplication}
          selectDbComboBox={selectDBAndFetchDefaultRoots}
          data-testid="db-app-dropdown"
        />
      </div>
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
        <FormDropDown
          form={objectSource.form}
          dbApplication={objectSource.dbApplication}
          onFormClick={onFormClick}
          isComponentInstance={isComponentInstance}
          data-testid="form-dropdown"
        />
      </div>
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
        <DropDownSheet
          isEdit={props.isEdit}
          sheet={objectSource.sheet}
          form={objectSource.form}
          dbApplication={objectSource.dbApplication}
          selectSheetComboBox={selectSheetComboBox}
          initAreas={initAreas}
          type="AMOUNT"
          objectSource={objectSource.objectSource}
          sheetsAreaChartMap={sheetsAreaChartMap}
          isFileExcelExisting={isFileExcelExisting}
          isComponentInstance={isComponentInstance}
          isLoadingDataExcel={isLoadingDataExcel}
          setIsLoadingDataExcel={setIsLoadingDataExcel}
          setIsLoadingSheetAndArea={props.setIsLoadingSheetAndArea}
          data-testid="sheet-dropdown"
        />
      </div>
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
        <ChoiceGroup
          selectedKey={selectedChoice}
          disabled={
            objectSource.sheet == null ||
            !isFileExcelExisting ||
            isComponentInstance ||
            isLocked ||
            (props.isEdit && objectSource.sheet?.code === objectSource.sheet?.desc && hasSheetToBeSelected)
          }
          styles={{
            flexContainer: {
              display: "flex",
              justifyContent: "left",
              columnGap: "0.5rem",
              flexWrap: "wrap",
            },
          }}
          options={choiceOptions}
          onChange={onChangeChoice}
        />
      </div>
      <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">{renderAreaOrCellSelection()}</div>
    </div>
  );
};
